import { useMemo } from 'react';
import type { BuildKickoffWatchdogState } from './run-state';

/**
 * Build-kickoff watchdog derivation (#3108).
 *
 * Extracted from Workspace.tsx (the `use-build-escalation-clock` precedent) so
 * the two properties this module owns are unit-testable without rendering all
 * of Workspace:
 *
 * 1. ESCALATION TIMING — `deriveBuildKickoffWatchdogFields` carries the exact
 *    pre-existing inline-memo logic (pending → stalled at
 *    `committedAt + BUILD_KICKOFF_STALL_MS`, resume-attempted latched by the
 *    retry ref). It must stay byte-for-byte equivalent: the watchdog is a
 *    separate escalation mechanism and this refactor must not change WHEN it
 *    escalates.
 *
 * 2. REFERENTIAL STABILITY — `useBuildKickoffWatchdog` returns an object whose
 *    IDENTITY changes only when an observable field (`status` / `committedAt` /
 *    `stalledAt`) changes. The pre-#3108 inline `useMemo` depended on the raw
 *    1s `buildKickoffClockNow` tick, so it rebuilt a NEW object every second at
 *    unchanged status; that object is a dependency of the `workspaceRunState`
 *    useMemo, which feeds `usePreviewModule`, so the whole preview-derivation
 *    path churned once per second for the entire kickoff-tracking window.
 */

// crucible#3106 — was 15_000. That value coincided almost exactly with how
// long a real kickoff can legitimately take before its first liveness signal
// (a running activitySnapshot row, or the poll-detected in-flight build)
// arrives — so on a slower run the watchdog would decide the kickoff had
// silently failed and fire a redundant 'auto-resume' retry against a kickoff
// that was actually fine, just slow to report. That collision (not a genuine
// stall) is what produced the intermittent 'needs-attention' after "Build
// kickoff retry sent." in gui-northstar-nospend.spec.ts (~25% locally: 1
// fail in 4 runs at 15_000; 0 fails in 6 runs at 45_000). Bumped to build in
// real margin above that legitimate-latency window.
export const BUILD_KICKOFF_STALL_MS = 45_000;

export interface BuildKickoffWatchdogInputs {
  /** `shouldTrackBuildKickoff(...)` AND a non-null attempt key. */
  tracking: boolean;
  /** First-seen wall-clock for the attempt key (undefined until observed). */
  committedAt: number | undefined;
  /** Has the auto-resume retry already fired for this attempt key? */
  resumeAttempted: boolean;
  /** The 1s kickoff clock reading (setState-ticked while committed). */
  clockNow: number;
}

/**
 * Derive the watchdog's observable fields for one render.
 *
 * Pure port of the pre-#3108 inline memo body — same predicates, same order,
 * same `clockNow >= committedAt + BUILD_KICKOFF_STALL_MS` stall boundary, same
 * returned shapes (a pending state carries `committedAt` only once observed and
 * never `stalledAt`).
 *
 * Args:
 *   input: Tracking gate, first-seen stamp, resume latch, and clock reading.
 *
 * Returns:
 *   A fresh watchdog state, or undefined when the watchdog is not tracking.
 */
export function deriveBuildKickoffWatchdogFields(
  input: BuildKickoffWatchdogInputs,
): BuildKickoffWatchdogState | undefined {
  if (!input.tracking) return undefined;
  const { committedAt } = input;
  if (committedAt === undefined) {
    return { status: 'pending' };
  }
  const stalledAt = committedAt + BUILD_KICKOFF_STALL_MS;
  if (input.resumeAttempted) {
    return { status: 'resume-attempted', committedAt, stalledAt };
  }
  if (input.clockNow >= stalledAt) {
    return { status: 'stalled', committedAt, stalledAt };
  }
  return { status: 'pending', committedAt };
}

/**
 * Referentially stable build-kickoff watchdog (#3108).
 *
 * Re-derives the observable fields on every render (cheap primitive reads —
 * exactly what the old inline memo did on every 1s tick anyway), but keys the
 * returned OBJECT's identity on those fields alone. A same-status 1s tick
 * therefore returns the IDENTICAL object, so the `workspaceRunState` useMemo
 * (which lists this object as a dependency) does not re-derive and the
 * `usePreviewModule` path stays quiet. Escalation transitions still land on
 * exactly the same tick: the stall comparison consumes the same `clockNow`
 * state value the old memo did.
 *
 * Args:
 *   input: Tracking gate, first-seen stamp, resume latch, and clock reading.
 *
 * Returns:
 *   The watchdog state (identity-stable at unchanged fields), or undefined.
 */
export function useBuildKickoffWatchdog(
  input: BuildKickoffWatchdogInputs,
): BuildKickoffWatchdogState | undefined {
  const fields = deriveBuildKickoffWatchdogFields(input);
  const status = fields?.status;
  const committedAt = fields?.committedAt;
  const stalledAt = fields?.stalledAt;
  return useMemo<BuildKickoffWatchdogState | undefined>(() => {
    if (status === undefined) return undefined;
    // Rebuild the exact derived shape: keys are present only when defined, so
    // consumers (and toStrictEqual pins) see the same object the derivation
    // produced — just with an identity that survives same-field ticks.
    const state: BuildKickoffWatchdogState = { status };
    if (committedAt !== undefined) state.committedAt = committedAt;
    if (stalledAt !== undefined) state.stalledAt = stalledAt;
    return state;
  }, [status, committedAt, stalledAt]);
}
